import { BOAT as ROWING_BOAT, PLAYER, WORLD } from '../core/config';
import { layoutToWorld } from '../core/coords';
import type { Layout } from '../data/types';
import type { Vec2, Vec3 } from '../geo/types';
import { createStreetRelief } from '../site/streetRelief';
import { waterAccesses } from '../site/waterAccess';
import { damDeckProfile } from '../site/damDeck';
import { toLayout } from '../world/fabric/landmarks/planFrame';
import type { TourRoute } from './types';
import { stadhuisPlanOf } from '../world/fabric/landmarks/stadhuis/stadhuisPlan';
import { TOUR_SUMMARIES, tourChapters } from './tourStories';
import { roundedCircuit, guidedTargets, gazeNear, landmarkSight, type Gaze } from './tourPath';
import { churchRoute, type ChurchRouteContext } from './churchRoute';

const WALK: readonly Vec2[] = [
  [0, -20],
  [-10, -5],
  [0, 28],
  [30, 23],
  [37.8, 16.4],
  [51.2, 3],
  [53.9, -14.1],
  [66, -27],
  [60, -36],
  [34.1, -33.9],
  [20.2, -36.4],
  [13.2, -29.3],
];

const BOAT: readonly Vec2[] = [
  [398, 466],
  [424.21, 442.28],
  [406.46, 420.63],
  [388.72, 398.96],
  [367, 358],
  [329, 303],
  [303.0882742449396, 280.85445278538657],
  [289.5448527573993, 261.0409287573184],
  [257, 235],
  [217, 190],
  [184, 142],
  [169.06258572321903, 118.69733016997952],
  [154.13741427678096, 99.90266983002047],
  [135, 80],
  [106, 40],
  [78, 4],
];

const AERIAL: readonly Vec3[] = [
  [580, 900, 280],
  [560, 560, 140],
  [650, 280, 120],
  [300, -80, 125],
  [80, -170, 105],
  [-180, 200, 165],
  [-170, 470, 240],
  [80, 740, 280],
  [380, 950, 310],
  [580, 900, 280],
];

function flyoverRoute(layout: Layout): TourRoute {
  return {
    id: 'flyover',
    summary: TOUR_SUMMARIES.flyover,
    chapters: tourChapters('flyover', []),
    title: 'Fly over Amsterdam',
    kind: 'fly',
    duration: 120,
    closed: true,
    positions: AERIAL.map(([x, y, height]) => layoutToWorld([x, y], height)),
    targets: [
      layoutToWorld([170, 40], 15),
      layoutToWorld([415, 414], 5),
      landmarkSight(layout, 'landmark-oude-kerk', [371, 140.7], 20),
      landmarkSight(layout, 'landmark-dam', [60, -30], 12),
      landmarkSight(layout, 'landmark-nieuwe-kerk', [-55.9, 94.1], 20),
      layoutToWorld([-10, 40], 16),
      layoutToWorld([160, -10], 12),
      layoutToWorld([415, 414], 8),
      layoutToWorld([350, 300], 15),
      layoutToWorld([170, 40], 15),
    ],
  };
}

function walkingGazes(layout: Layout, path: readonly Vec2[]): readonly Gaze[] {
  const hall = layout.landmarks.find((l) => l.id === 'landmark-oude-stadhuis');
  const plan = hall && stadhuisPlanOf(hall);
  const tower = plan
    ? toLayout(plan.frame.plan, [(plan.tower.u0 + plan.tower.u1) / 2, plan.tower.v0])
    : ([-30, -7] as Vec2);
  const townhall = layoutToWorld(tower, 16);
  return [
    gazeNear(path, [-10, -5], townhall, 27),
    gazeNear(path, [0, 28], landmarkSight(layout, 'landmark-nieuwe-kerk', [-55.9, 94.1], 18), 20),
    gazeNear(path, [30, 23], layoutToWorld([55.3, 11.3], 13), 23),
    gazeNear(path, [53.9, -14.1], layoutToWorld([68, -11.3], 5), 18),
    gazeNear(path, [60, -36], layoutToWorld([27.7, -12.1], 12), 20),
    gazeNear(path, [13.2, -29.3], townhall, 22),
  ];
}

function walkingRoute(layout: Layout): TourRoute {
  const relief = createStreetRelief(layout);
  const path = roundedCircuit(WALK, 1, 0.35);
  const height = (point: Vec2) => relief(point) + PLAYER.eyeHeight;
  return {
    id: 'walk',
    summary: TOUR_SUMMARIES.walk,
    chapters: tourChapters('walk', path),
    title: 'Walk the Dam',
    kind: 'walk',
    duration: 240,
    closed: true,
    path: 'linear',
    positions: path.map((point) => layoutToWorld(point, height(point))),
    targets: guidedTargets(path, height, 8, walkingGazes(layout, path), true, true),
  };
}

/* The boat's circuit: down the Damrak, a turn before the dam's sluice, back and a turn in the IJ.
   The bridge passages retain their surveyed straight lines; curves finish before their decks. */
function boatCircuit(): readonly Vec2[] {
  const points: readonly Vec2[] = [
    ...BOAT,
    [80, 5],
    [79, -1],
    [84, -3],
    [89, 1],
    [91, 9],
    [92, 25],
    ...BOAT.slice(0, -1).reverse(),
    [384, 479],
    [380, 493],
    [388, 503],
    [401, 498],
    [409, 485],
  ];
  const passages = new Set(BOAT.slice(6, 8).concat(BOAT.slice(11, 13)));
  return roundedCircuit(
    points,
    points.map((p) => (passages.has(p) ? 0 : 8)),
    0.5
  );
}

function boatRoute(layout: Layout): TourRoute {
  const path = boatCircuit();
  const westStairs = waterAccesses(layout).find(
    (flight) => flight.id === 'nieuwebrug-west-water-stairs'
  );
  const stairsSight = layoutToWorld(westStairs?.origin ?? [379.962, 452.961], 0);
  const dam = layout.landmarks.find((landmark) => landmark.kind === 'dam');
  let sluice: Vec3 = layoutToWorld([65, -18], 1.5);
  if (dam) {
    const profile = damDeckProfile(dam);
    const u = (profile.u0 + profile.u1) / 2 - Math.min(3, (profile.u1 - profile.u0) / 10);
    sluice = layoutToWorld(toLayout(profile.plan, [u, profile.v1]), 1.5);
  }
  return {
    id: 'boat',
    summary: TOUR_SUMMARIES.boat,
    chapters: tourChapters('boat', path),
    title: 'Boat on the Damrak',
    kind: 'boat',
    duration: 420,
    closed: true,
    path: 'linear',
    positions: path.map((point) => layoutToWorld(point, WORLD.waterY + ROWING_BOAT.eyeHeight)),
    targets: guidedTargets(
      path,
      () => WORLD.waterY + ROWING_BOAT.eyeHeight,
      12,
      [
        { at: 40, span: 30, target: stairsSight, weight: 0.25 },
        gazeNear(path, [106, 40], sluice, 25, 0.5),
      ],
      true
    ),
  };
}

export function createTourRoutes(
  layout: Layout,
  walking?: ChurchRouteContext
): readonly TourRoute[] {
  const routes = [flyoverRoute(layout), walkingRoute(layout), boatRoute(layout)];
  if (walking) routes.push(churchRoute(layout, walking));
  return routes;
}
